// Defers a side effect to the first idle moment after the page has painted.
//
// Why this exists: Lighthouse's mobile score does not measure the paint we observe, it replays the
// dependency graph on a simulated throttled link (Lantern). Every request observed before the LCP paint
// is charged against LCP, so a `fetch` fired during hydration inflates the score even when the paint
// does not depend on it — the home page was losing the whole 25% LCP weight this way. Anything that is
// not needed for the first paint therefore has to start after it, not merely "async".
//
// `requestIdleCallback` is missing on Safari before 17, hence the timeout fallback. The exact delay does
// not matter, only that it lands after the paint. Returns a cancel function for effect cleanup.
const IDLE_FALLBACK_DELAY_MS = 1200;
const IDLE_TIMEOUT_MS = 3000;

export function runWhenIdle(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;

  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
    return () => window.cancelIdleCallback(handle);
  }

  const timer = window.setTimeout(callback, IDLE_FALLBACK_DELAY_MS);
  return () => window.clearTimeout(timer);
}
